import { yupResolver } from '@hookform/resolvers/yup';
import { Checkbox } from 'components/Form/Checkbox';
import { Select } from 'components/Form/Select';
import { useCallClientNumbers } from 'hooks/fetch/useCallClientNumbers';
import { useTemplates } from 'hooks/fetch/useTemplates';
import { useContacts } from 'hooks/useContacts';
import { useMessages } from 'hooks/useMessages';
import { useEffect, useState } from 'react';
import { FieldValues, FormProvider, SubmitHandler, useForm, useWatch } from "react-hook-form";
import Modal from 'react-modal';
import { api } from 'services/api';
import { Button } from 'styles/Button';
import { Form } from 'styles/Form';
import { InputGroup } from 'styles/InputGroup';
import { TemplateTextarea } from 'styles/TemplateTextarea';
import { ModalTitle } from 'styles/Modal';
import { ChatMessage } from 'types/ChatMessage';
import { WhatsAppNumber } from 'types/WhatsAppNumber';
import { isEmpty } from 'utils/Misc';
import { handleRequestError } from 'utils/Request';
import { v4 as uuidv4 } from 'uuid';
import closeImg from '../../assets/close.svg';
import { Header, OpeningMessageTextarea } from './styles';
import { CustomTemplateData, TemplateModalProps, SelectTemplateData } from './types';
import { templateMessageValidator } from './validation';
import { Template } from 'types/Template';
import { Input } from 'components/Form/Input';

const validationSchema = templateMessageValidator();

function numberLabel(number: WhatsAppNumber) {
  return number.phone_number ? `${number.label} (${number.phone_number})` : number.label;
}

function buildOpeningMessage(name: string) {
  return `Olá, ${name}, tudo bem? Aqui é do suporte da Tecimob.\n\nEstamos usando este número auxiliar para dar continuidade ao seu atendimento.\n\nPodemos seguir por aqui?`;
}

function wait(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function TemplateModal({ isOpen, onRequestClose }: TemplateModalProps) {
  const { data, isLoading } = useTemplates();
  const { data: numbers } = useCallClientNumbers();

  const {
    messagesQuery: { query },
    selectedQuotedMessage,
    setSelectedQuotedMessage,
    setPreviewMessages,
    setIsRefetchingMessages,
  } = useMessages();

  const { selectedContact } = useContacts();

  const [selectedNumberId, setSelectedNumberId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);

  const form = useForm<FieldValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      customTemplate: '',
      openingMessage: '',
      openingMessageEnabled: false,
    },
  });

  const { handleSubmit, setValue, control, reset, formState } = form;

  const isOpeningMessageEnabled = useWatch({ name: 'openingMessageEnabled', control });

  const selectedNumber = numbers?.find(number => number.number_id === selectedNumberId);
  const isCloud = !selectedNumber || selectedNumber.provider === 'whatsapp-cloud';

  useEffect(() => {
    if (!isOpen || !numbers || numbers.length === 0) return;

    const matchBySession = numbers.find(number => number.number_id === selectedContact.session);
    const defaultNumber = matchBySession ?? numbers.find(number => number.provider === 'whatsapp-cloud') ?? numbers[0];

    setSelectedNumberId(defaultNumber.number_id);
    setValue('number_id', defaultNumber.number_id);
  }, [isOpen, numbers, selectedContact, setValue]);

  // Número auxiliar (Relacionamento/WAHA): ativa e pré-preenche a mensagem de
  // abertura padrão; número Padrão não usa esse recurso
  useEffect(() => {
    if (!isOpen) return;

    if (isCloud) {
      setValue('openingMessageEnabled', false);
      return;
    }

    setValue('openingMessageEnabled', true);

    if (!formState.dirtyFields.openingMessage) {
      const name = selectedContact.name_person || selectedContact.name;
      setValue('openingMessage', buildOpeningMessage(name));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isCloud, selectedContact.id]);

  function handleRequestClose() {
    setSelectedTemplate(null);
    reset();
    onRequestClose();
  }

  async function applyTemplate(template: Template, numberId: string) {
    const namePerson = selectedContact.name_person || selectedContact.name;

    setValue('name_person', namePerson);
    setValue('name_meta', template.name_meta ?? '', { shouldDirty: true });

    const number = numbers?.find(n => n.number_id === numberId);

    if (!number || number.provider === 'whatsapp-cloud') {
      setValue('customTemplate', template.message ?? '', { shouldDirty: true });
      return;
    }

    try {
      const { data: rendered } = await api.post('/messages/render-template', {
        contactId: selectedContact.id,
        name_meta: template.name_meta,
        name_person: namePerson,
      });
      setValue('customTemplate', rendered.body ?? '', { shouldDirty: true });
    } catch (error) {
      handleRequestError(error, 'Erro ao carregar o template. Por favor, tente novamente!');
    }
  }

  function handleSelectTemplate(newValue: unknown) {
    const template = (newValue as SelectTemplateData).value;
    setSelectedTemplate(template);
    applyTemplate(template, selectedNumberId);
  }

  function handleSelectNumber(newValue: unknown) {
    const numberId = (newValue as { value: string }).value;
    setSelectedNumberId(numberId);

    if (selectedTemplate) {
      applyTemplate(selectedTemplate, numberId);
    }
  }

  async function handleSendTemplateMessage({ name_meta, customTemplate, name_person, number_id, openingMessage, openingMessageEnabled }: CustomTemplateData) {
    const quoted = selectedQuotedMessage;
    const shouldSendOpeningMessage = !isCloud && openingMessageEnabled;

    setSelectedQuotedMessage({} as ChatMessage);
    setPreviewMessages(prevMessages => {
      const newMessages: ChatMessage[] = [];

      if (shouldSendOpeningMessage) {
        newMessages.push({
          id: uuidv4(),
          body: openingMessage,
          contact_id: selectedContact.id,
          type: 'chat',
          quoted: null,
          caption: '',
          is_forwarded: false,
          time: new Date().toString(),
          from_me: true,
          is_read: true,
          ack: 'SENT',
        } as ChatMessage);
      }

      newMessages.push({
        id: uuidv4(),
        body: customTemplate,
        contact_id: selectedContact.id,
        type: 'chat',
        quoted: !isEmpty(quoted) ? quoted : '',
        caption: '',
        is_forwarded: false,
        time: new Date().toString(),
        from_me: true,
        is_read: true,
        ack: 'SENT',
      } as ChatMessage);

      return [...prevMessages, ...newMessages];
    });

    try {
      if (shouldSendOpeningMessage) {
        await api.post('/messages/send-text', {
          contactId: selectedContact.id,
          body: openingMessage,
          quotedMsgId: null,
          isAnnotation: false,
          number_id: number_id,
        });

        await wait(2000);
      }

      if (isCloud) {
        await api.post('/messages/send-template', {
          contactId: selectedContact.id,
          name_meta: name_meta,
          name_person: name_person,
        });
      } else {
        await api.post('/messages/send-text', {
          contactId: selectedContact.id,
          body: customTemplate,
          quotedMsgId: !isEmpty(quoted) ? quoted.whatsapp_msg_id : null,
          isAnnotation: false,
          number_id: number_id,
        });
      }

      setIsRefetchingMessages(true);
      await query.refetch();
      setPreviewMessages([]);
      setIsRefetchingMessages(false);
    } catch (error) {
      handleRequestError(error, 'Erro ao enviar mensagem. Por favor, tente novamente!');
    }

    setSelectedTemplate(null);
    reset();
    handleRequestClose();
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={handleRequestClose}
        overlayClassName="react-modal-overlay"
        className="react-modal-content big"
      >
        <button
          type="button"
          onClick={handleRequestClose}
          className="react-modal-close"
        >
          <img src={closeImg} alt="Fechar modal" loading="lazy" />
        </button>

        <div>
          <Header>
            <ModalTitle noSpacing>Enviar template</ModalTitle>
          </Header>
          <Form onSubmit={handleSubmit(handleSendTemplateMessage as SubmitHandler<FieldValues>)}>
            <FormProvider {...form}>
              <InputGroup>
                <Select
                  control={control}
                  name="number_id"
                  id="number_id"
                  label="Número de saída"
                  placeholder="Selecionar número de saída"
                  onSelect={handleSelectNumber}
                  fullwidth
                  config={{
                    noOptionsMessage: () => 'Nenhum número disponível.',
                    options: numbers ? numbers.map(number => ({ value: number.number_id, label: numberLabel(number) })) : [],
                    closeMenuOnSelect: true,
                  }}
                />
              </InputGroup>
              {!isCloud && (
                <>
                  <InputGroup>
                    <Checkbox
                      control={control}
                      name="openingMessageEnabled"
                      label="Enviar mensagem de abertura padrão"
                      id="openingMessageEnabled"
                    />
                  </InputGroup>
                  {isOpeningMessageEnabled && (
                    <InputGroup>
                      <OpeningMessageTextarea
                        control={control}
                        name="openingMessage"
                        id="openingMessage"
                        rows={4}
                        fullwidth
                      />
                    </InputGroup>
                  )}
                </>
              )}
              <InputGroup>
                <Select
                  control={control}
                  name="selectTemplate"
                  id="selectTemplate"
                  label="Selecionar templates"
                  placeholder="Selecionar template"
                  onSelect={handleSelectTemplate}
                  fullwidth
                  config={{
                    noOptionsMessage: () => 'Nenhum template disponível.',
                    options: data ? data.map(template => ({ value: template, label: template.title })) : [],
                    closeMenuOnSelect: true,
                    isDisabled: isLoading,
                  }}
                >
                  {data && data.map((template: Template) => (
                    <option key={template.id} value={template.id}>
                      {template.title}
                    </option>
                  ))}
                </Select>
              </InputGroup>
              {isCloud && (
                <InputGroup>
                  <Input
                    control={control}
                    type="text"
                    name="name_person"
                    id="name_person"
                    label="Nome da pessoa"
                    placeholder="Nome da pessoa"
                    maxLength={255}
                    fullwidth
                  />
                </InputGroup>
              )}
              <InputGroup>
                <TemplateTextarea
                  control={control}
                  name="customTemplate"
                  id="customTemplate"
                  label="Mensagem"
                  placeholder="Mensagem do template"
                  rows={6}
                  fullwidth
                  disabled={isCloud}
                />
              </InputGroup>
              <InputGroup>
                <Button
                  type="submit"
                  fullwidth
                  disabled={formState.isSubmitting}
                >
                  {formState.isSubmitting ? 'Carregando...' : 'Enviar'}
                </Button>
              </InputGroup>
            </FormProvider>
          </Form>
        </div>
      </Modal>
    </>
  );
}
